// CLIENT PORTAL — the location multi-select's own rules (§9, 2026-09-03).
//
// PURE, and extracted rather than left inside the component, for the reason
// CLAUDE.md records: asserting these through a rendered picker needs jsdom and
// CLICKS, and this repo sits one jsdom file from the documented 5s-timeout
// cliff. The rules are the valuable part and they are checkable without one.
//
// ⚠️⚠️ NOTHING HERE IS A SECURITY BOUNDARY. The browser can send any ids it
// likes; the SERVER resolves the granted scope from the contact's account links,
// INTERSECTS the request with it, and 404s anything outside — see
// `selectManyWithinScope`. This decides what the PICKER shows and sends, which
// is a convenience over that boundary, never a substitute for it.

/**
 * ⚠️⚠️ `null` AND `[]` ARE DIFFERENT STATES, all the way to the server.
 *
 *   · `null` — no filter. Every location, and the parameter is absent.
 *   · `[]`   — filtered to nothing. The parameter is PRESENT and empty, and the
 *              report reads nothing.
 *
 * Collapsing them would answer a request for nothing with everything the client
 * holds — the same `empty ≠ absent` rule `campaignIds` draws, and the one this
 * codebase has been bitten by twice.
 */
export type LocationSelection = string[] | null;

/**
 * The URL parameters after a selection change.
 *
 * ⚠️⚠️ AN EMPTY SELECTION STILL WRITES THE PARAMETER, EMPTY. Deleting it would
 * mean "all locations" — the exact opposite of what a reader just asked for by
 * unticking everything, and the request would look perfectly ordinary on the
 * way out.
 *
 * ⚠️ AND IT CLEARS THE SINGLE-ID DRILL-DOWN. `?account=` and `?accounts=` are
 * different questions about the same thing; a URL carrying both would show the
 * drill-down's answer under the picker's chips. The server checks the multi
 * parameter first, so this keeps the URL honest rather than deciding anything.
 *
 * ⚠️ PURE and given the previous params, so it is testable without a router —
 * the rule is the valuable part and it does not need jsdom to check.
 */
export function nextLocationParams(
  prev: URLSearchParams,
  ids: LocationSelection,
  keys: { many: string; one: string },
): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (ids === null) {
    next.delete(keys.many);
    return next;
  }
  next.set(keys.many, ids.join(','));
  next.delete(keys.one);
  return next;
}

/** Whether a location is currently included. `null` means all of them are. */
export function isLocationSelected(selection: LocationSelection, id: string): boolean {
  return selection === null || selection.includes(id);
}

/**
 * The selection after ticking or unticking one location.
 *
 * ⚠️⚠️ EVERYTHING TICKED COLLAPSES BACK TO `null` — "no filter", not "a filter
 * that happens to name them all". A client who later gains a location then sees
 * it, instead of being silently excluded by a bookmark that named the old set.
 *
 * ⚠️⚠️ AND UNTICKING THE LAST ONE YIELDS `[]`, NEVER `null`. That is the
 * dangerous direction: `null` there would answer "none of them" with every
 * location the client holds.
 */
export function toggleLocation(
  selection: LocationSelection,
  id: string,
  allIds: readonly string[],
): LocationSelection {
  const current = selection === null ? [...allIds] : selection;
  const next = isLocationSelected(selection, id)
    ? current.filter((x) => x !== id)
    : [...current, id];
  return next.length === allIds.length ? null : next;
}

/**
 * Do two selections mean the same thing?
 *
 * ⚠️⚠️ SET-BASED, NOT ORDER-BASED. The array is built in tick order, so
 * [a, b] and [b, a] are the same selection stored two ways — and Save is
 * disabled on "unchanged", so an order-sensitive comparison would leave Save
 * live after a tick and an untick that cancel out, committing a write nobody
 * asked for.
 *
 * ⚠️ `null` IS NOT `[]` AND NEITHER IS A LIST OF EVERY ID. `null` means no
 * filter; `[]` means filtered to nothing. `toggleLocation` already collapses
 * all-ticked back to `null`, so a full list never reaches here — but if one
 * did, treating it as equal to `null` would be the `empty ≠ absent` collapse
 * from the other end.
 */
export function sameLocationSelection(a: LocationSelection, b: LocationSelection): boolean {
  if (a === null || b === null) return a === b;
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

/** Whether Save may commit, and why not when the reason is worth saying. */
export interface LocationSaveState {
  canSave: boolean;
  /**
   * ⚠️ NULL WHERE THE CONTROL ALREADY EXPLAINS ITSELF. A disabled Save beside
   * an unchanged selection needs no sentence; a disabled Save after somebody
   * pressed "None" does, because nothing on screen says why.
   */
  reason: string | null;
}

/**
 * ⚠️⚠️ SAVE REFUSES AN EMPTY SELECTION.
 *
 * An empty selection returns an empty report. Before the picker batched, that
 * was committed the instant the last box was unticked and the heading said so
 * after the fact; with a Save step the state is reachable in the DRAFT and can
 * be refused before it becomes anybody's report.
 *
 * ⚠️ THE EMPTY CHECK COMES FIRST, deliberately: a forwarded `?accounts=` link
 * can make the APPLIED selection empty too, and then "unchanged" would be the
 * matching verdict — leaving a client looking at an empty report with a
 * disabled Save and nothing telling them how to escape it.
 *
 * ⚠️ `locationSelectionLabel`'s empty branch STAYS for exactly that case: the
 * URL can still carry it, so the heading must still be able to name it.
 */
export function locationSaveState(
  draft: LocationSelection,
  applied: LocationSelection,
): LocationSaveState {
  if (draft !== null && draft.length === 0) {
    return { canSave: false, reason: 'Choose at least one location.' };
  }
  if (sameLocationSelection(draft, applied)) return { canSave: false, reason: null };
  return { canSave: true, reason: null };
}

/**
 * WHAT THE REPORT IS ACTUALLY NARROWED TO (§B, 2026-09-03).
 *
 * ⚠️⚠️ ONE RULE, ONE PLACE, AND IT IS READ BY BOTH THE HEADING AND THE NAV.
 * Before this existed, the heading derived its own answer from the `accounts`
 * URL parameter alone — so a nav drill-down, which writes `account`, produced an
 * empty selection and `locationSelectionLabel(null, …)` printed
 * "All 6 locations" over one club's report. The report narrowed correctly; only
 * its name was wrong.
 *
 * ⚠️⚠️ THE SERVER'S ANSWER WINS, AND IT IS NOT RE-DERIVED HERE. `resolveScope`
 * owns the precedence between the two URL parameters (`accountIds` first, then
 * `account`, null for a true multi-selection because "a set of clubs is not one
 * club") and ships it on the payload. This reads that answer; it does not hold a
 * second opinion about which parameter wins. The server is the only thing that
 * knows what it actually narrowed to, so a heading derived from the URL can
 * always disagree with the report beneath it.
 *
 * ⚠️ THE URL SELECTION IS STILL NEEDED, and not as a fallback for the
 * precedence: the payload says `null` for BOTH "the whole scope" and "a subset
 * of it", and those are different headings and different active rows. Only the
 * selection can tell them apart.
 *
 * ⚠️ `subset` DELIBERATELY MATCHES NO NAV ROW. Highlighting "All locations" for
 * a two-of-three selection would state something untrue, and there is no single
 * club row that is right either — so no row is active, which is the honest
 * rendering of "you are looking at several".
 */
export type PortalNavSelection =
  /** Exactly one club — its row is active and the heading is its name. */
  | { kind: 'account'; accountId: string }
  /** The whole scope — the "All locations" row. */
  | { kind: 'all' }
  /** Several, but not all. No row is right. */
  | { kind: 'subset' };

export function resolvePortalNavSelection(
  /** The payload's own `selectedAccountId`. `undefined` = no report loaded yet. */
  payloadSelectedAccountId: string | null | undefined,
  urlSelection: LocationSelection,
): PortalNavSelection {
  if (payloadSelectedAccountId) return { kind: 'account', accountId: payloadSelectedAccountId };
  // ⚠️ Before the payload arrives there is no report to name, but the nav is
  // already on screen — so a single-id URL selection stands in until it does,
  // rather than flashing "All locations" over a club's report.
  if (payloadSelectedAccountId === undefined && urlSelection?.length === 1) {
    return { kind: 'account', accountId: urlSelection[0] as string };
  }
  if (urlSelection === null) return { kind: 'all' };
  return { kind: 'subset' };
}

/**
 * What the heading says.
 *
 * ⚠️ AN EMPTY SELECTION IS NAMED rather than left blank. It returns an empty
 * report, and a reader who unticked everything by accident needs the heading to
 * say so rather than the page to look broken.
 */
export function locationSelectionLabel(
  selection: LocationSelection,
  locations: ReadonlyArray<{ id: string; name: string }>,
): string {
  if (selection === null) return `All ${locations.length} locations`;
  if (selection.length === 0) return 'No locations selected';
  if (selection.length === 1) {
    return locations.find((l) => l.id === selection[0])?.name ?? '1 location';
  }
  return `${selection.length} of ${locations.length} locations`;
}

/**
 * THE HEADING OVER THE REPORT (§B, 2026-09-03).
 *
 * ⚠️⚠️ THE PAYLOAD'S ONE CLUB WINS. This is the fix for "All 6 locations" over
 * Kalgoorlie: the heading used to be `locationSelectionLabel(selection, …)`
 * alone, and `selection` comes from the `accounts` URL parameter — empty on a
 * nav drill-down, which writes `account`. The label then returned
 * `All ${n} locations`, and the report underneath it was one club's.
 *
 * ⚠️ `all` AND `subset` FALL THROUGH TO THE LABEL, because those are the two
 * states the server reports identically as null and only the selection can tell
 * apart or count.
 *
 * ⚠️ PURE, so the sentence is asserted without jsdom and without a react-query
 * provider — the same reason the toggle rules live here rather than in the
 * picker. This repo sits one jsdom file from the documented 5s-timeout cliff.
 */
export function locationHeading(
  navSelection: PortalNavSelection,
  selection: LocationSelection,
  locations: ReadonlyArray<{ id: string; name: string }>,
): string {
  if (navSelection.kind === 'account') {
    const named = locations.find((l) => l.id === navSelection.accountId)?.name;
    // ⚠️ A club the picker does not know (a stale link to one the client no
    // longer holds) falls back rather than rendering "undefined". The server
    // refuses that request anyway; the heading must not crash on the way there.
    if (named) return named;
  }
  return locationSelectionLabel(selection, locations);
}
